import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  MessageSquare,
  Send,
  Search,
  Phone,
  User,
  Clock,
  Check,
  CheckCheck,
  AlertCircle,
  RefreshCw,
  FileText,
  Plus,
  Trash2,
  Edit3,
  X,
  ChevronLeft,
  BarChart3,
  Loader2,
  Ban,
  Sparkles,
  MessageCircle,
  ArrowUpRight,
  ArrowDownLeft,
} from "lucide-react";
import {
  getInbox,
  getConversation,
  getSMSHistory,
  sendSMS,
  sendSMSTemplate,
  getUnreadCount,
  markAsRead,
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getStats,
  checkOptOut,
  calculateSegments,
  calculateCredits,
  formatPhoneNumber,
} from "../api/sms";

// ============ SUB-COMPONENTS ============

const StatsCard = ({ label, value, icon: Icon, color = "sky", subValue }) => {
  const colorClasses = {
    sky: "bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400",
    emerald: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400",
    amber: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
    red: "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400",
    purple: "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400",
  };

  return (
    <div className="flex-1 min-w-[120px] p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
          {subValue && <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>}
        </div>
      </div>
    </div>
  );
};

const DeliveryStatusBadge = ({ status }) => {
  const config = {
    QUEUED: { label: "Queued", icon: Clock, color: "text-slate-500" },
    SENT: { label: "Sent", icon: Check, color: "text-sky-500" },
    DELIVERED: { label: "Delivered", icon: CheckCheck, color: "text-emerald-500" },
    FAILED: { label: "Failed", icon: AlertCircle, color: "text-red-500" },
    UNDELIVERED: { label: "Undelivered", icon: AlertCircle, color: "text-amber-500" },
  };

  const statusConfig = config[status] || config.QUEUED;
  const Icon = statusConfig.icon;

  return (
    <span className={`inline-flex items-center gap-1 text-xs ${statusConfig.color}`}>
      <Icon className="w-3 h-3" />
      {statusConfig.label}
    </span>
  );
};

const CharacterCounter = ({ message }) => {
  const { segments, chars, charsPerSegment } = calculateSegments(message);
  const credits = calculateCredits(message);
  const charsInCurrentSegment = chars % charsPerSegment || (chars > 0 ? charsPerSegment : 0);

  return (
    <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
      <span>
        {charsInCurrentSegment}/{charsPerSegment} chars
      </span>
      <span className="text-slate-300 dark:text-slate-600">|</span>
      <span>{segments} segment{segments !== 1 ? "s" : ""}</span>
      <span className="text-slate-300 dark:text-slate-600">|</span>
      <span className="text-amber-600 dark:text-amber-400">{credits} credits</span>
    </div>
  );
};

// ============ MAIN COMPONENT ============

const SMSInbox = ({ userEmail }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState("conversations"); // conversations, templates, stats

  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Compose state
  const [composePhone, setComposePhone] = useState("");
  const [composeMessage, setComposeMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [isOptedOut, setIsOptedOut] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: "", body: "", description: "", triggerType: "custom" });

  // Stats state
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Search
  const [searchTerm, setSearchTerm] = useState("");

  // Unread count
  const [unreadCount, setUnreadCount] = useState(0);

  // Refs
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  // ============ DATA FETCHING ============

  const fetchConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      // Get inbox (incoming) and history (outgoing) to build conversation list
      const [inboxResponse, historyResponse] = await Promise.all([
        getInbox({ limit: 100 }),
        getSMSHistory({ limit: 100 }),
      ]);

      const inboxMessages = inboxResponse?.messages || [];
      const historyMessages = historyResponse?.messages || [];

      // Combine and group by phone number
      const allMessages = [
        ...inboxMessages.map(m => ({ ...m, phone: m.from_phone })),
        ...historyMessages.map(m => ({ ...m, phone: m.recipient_phone })),
      ];

      // Group by phone and get latest message
      const conversationMap = new Map();
      allMessages.forEach(msg => {
        const phone = msg.phone?.replace(/\D/g, "") || "";
        if (!phone) return;

        const existing = conversationMap.get(phone);
        const msgTime = new Date(msg.sent_at || msg.received_at).getTime();

        if (!existing || msgTime > existing.lastTime) {
          conversationMap.set(phone, {
            phone,
            recipientName: msg.recipient_name || msg.from_phone,
            lastMessage: msg.message_body,
            lastTime: msgTime,
            direction: msg.direction,
            unread: msg.direction === "inbound" && !msg.read,
          });
        }
      });

      // Convert to array and sort by time
      const conversationList = Array.from(conversationMap.values())
        .sort((a, b) => b.lastTime - a.lastTime);

      setConversations(conversationList);

      // Update unread count
      const { unread_count } = await getUnreadCount();
      setUnreadCount(unread_count || 0);
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  const fetchMessages = useCallback(async (phone) => {
    if (!phone) return;
    setIsLoadingMessages(true);
    try {
      const response = await getConversation(phone, { limit: 100 });
      setMessages(response?.messages || []);

      // Mark as read
      const unreadIds = (response?.messages || [])
        .filter(m => m.direction === "inbound" && !m.read)
        .map(m => m.message_id);

      if (unreadIds.length > 0) {
        await markAsRead(unreadIds);
        setUnreadCount(prev => Math.max(0, prev - unreadIds.length));
      }

      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await getTemplates();
      setTemplates(response?.templates || []);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const response = await getStats();
      setStats(response);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Load messages when phone selected
  useEffect(() => {
    if (selectedPhone) {
      fetchMessages(selectedPhone);
    }
  }, [selectedPhone, fetchMessages]);

  // Load templates when tab changes
  useEffect(() => {
    if (activeTab === "templates" && templates.length === 0) {
      fetchTemplates();
    }
    if (activeTab === "stats" && !stats) {
      fetchStats();
    }
  }, [activeTab, templates.length, stats, fetchTemplates, fetchStats]);

  // Check opt-out status when phone changes
  useEffect(() => {
    const checkPhoneOptOut = async () => {
      const phone = selectedPhone || composePhone;
      if (phone && phone.replace(/\D/g, "").length >= 10) {
        try {
          const result = await checkOptOut(phone);
          setIsOptedOut(result?.opted_out || false);
        } catch {
          setIsOptedOut(false);
        }
      } else {
        setIsOptedOut(false);
      }
    };
    checkPhoneOptOut();
  }, [selectedPhone, composePhone]);

  // Poll for new messages every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversations();
      if (selectedPhone) {
        fetchMessages(selectedPhone);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchConversations, fetchMessages, selectedPhone]);

  // ============ HANDLERS ============

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const phone = selectedPhone || composePhone;
    if (!phone || !composeMessage.trim() || isSending || isOptedOut) return;

    setIsSending(true);
    setSendError(null);

    try {
      await sendSMS({
        toPhone: phone,
        message: composeMessage.trim(),
      });

      setComposeMessage("");

      // Refresh messages
      if (selectedPhone) {
        await fetchMessages(selectedPhone);
      } else {
        // New conversation - refresh and select
        await fetchConversations();
        setSelectedPhone(phone.replace(/\D/g, ""));
        setComposePhone("");
      }
    } catch (err) {
      setSendError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await updateTemplate(editingTemplate.template_id, {
          name: templateForm.name,
          body: templateForm.body,
          description: templateForm.description,
          triggerType: templateForm.triggerType,
        });
      } else {
        await createTemplate({
          name: templateForm.name,
          body: templateForm.body,
          description: templateForm.description,
          triggerType: templateForm.triggerType,
        });
      }

      await fetchTemplates();
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateForm({ name: "", body: "", description: "", triggerType: "custom" });
    } catch (err) {
      console.error("Failed to save template:", err);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await deleteTemplate(templateId);
      await fetchTemplates();
    } catch (err) {
      console.error("Failed to delete template:", err);
    }
  };

  const handleUseTemplate = (template) => {
    setComposeMessage(template.body);
    setActiveTab("conversations");
    messageInputRef.current?.focus();
  };

  // Filter conversations by search
  const filteredConversations = conversations.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.phone?.includes(search) ||
      c.recipientName?.toLowerCase().includes(search) ||
      c.lastMessage?.toLowerCase().includes(search)
    );
  });

  // ============ RENDER ============

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="flex-none p-6 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-sky-500/10 via-indigo-500/10 to-purple-500/10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-sky-600" />
              SMS Messages
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Two-way SMS messaging with your clients
            </p>
          </div>

          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <div className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm font-medium">
                {unreadCount} unread
              </div>
            )}
            <button
              onClick={() => {
                fetchConversations();
                if (selectedPhone) fetchMessages(selectedPhone);
              }}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab("conversations")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === "conversations"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-1.5" />
            Conversations
            {unreadCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === "templates"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1.5" />
            Templates
          </button>
          <button
            onClick={() => setActiveTab("stats")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              activeTab === "stats"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-1.5" />
            Statistics
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex min-h-0">
        {activeTab === "conversations" && (
          <>
            {/* Conversations List */}
            <div className="w-80 flex-none border-r border-slate-200 dark:border-slate-800 flex flex-col">
              {/* Search */}
              <div className="p-3 border-b border-slate-200 dark:border-slate-800">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search conversations..."
                    className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              </div>

              {/* New Message Button */}
              <div className="p-3 border-b border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => {
                    setSelectedPhone(null);
                    setMessages([]);
                    setComposePhone("");
                    setComposeMessage("");
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Message
                </button>
              </div>

              {/* Conversation List */}
              <div className="flex-1 overflow-y-auto">
                {isLoadingConversations ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <MessageSquare className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {searchTerm ? "No conversations found" : "No conversations yet"}
                    </p>
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <button
                      key={conv.phone}
                      onClick={() => setSelectedPhone(conv.phone)}
                      className={`w-full text-left p-4 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${
                        selectedPhone === conv.phone ? "bg-sky-50 dark:bg-sky-900/20" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                          {conv.recipientName?.[0]?.toUpperCase() || <User className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900 dark:text-white truncate">
                              {conv.recipientName || formatPhoneNumber(conv.phone)}
                            </p>
                            {conv.unread && (
                              <span className="w-2 h-2 rounded-full bg-sky-500 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {formatPhoneNumber(conv.phone)}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            {conv.direction === "outbound" ? (
                              <ArrowUpRight className="w-3 h-3 text-slate-400" />
                            ) : (
                              <ArrowDownLeft className="w-3 h-3 text-sky-500" />
                            )}
                            <p className="text-sm text-slate-600 dark:text-slate-300 truncate">
                              {conv.lastMessage}
                            </p>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(conv.lastTime).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Message Thread */}
            <div className="flex-1 flex flex-col min-w-0">
              {selectedPhone ? (
                <>
                  {/* Thread Header */}
                  <div className="flex-none p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedPhone(null)}
                        className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white font-medium">
                        <User className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {formatPhoneNumber(selectedPhone)}
                        </p>
                        {isOptedOut && (
                          <p className="text-xs text-red-500 flex items-center gap-1">
                            <Ban className="w-3 h-3" />
                            Opted out of SMS
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 dark:bg-slate-900/50">
                    {isLoadingMessages ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                        <p className="text-sm text-slate-500">No messages yet</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.message_id}
                          className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              msg.direction === "outbound"
                                ? "bg-sky-600 text-white rounded-br-md"
                                : "bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 rounded-bl-md"
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{msg.message_body}</p>
                            <div className={`flex items-center justify-end gap-2 mt-1 text-xs ${
                              msg.direction === "outbound" ? "text-sky-200" : "text-slate-400"
                            }`}>
                              <span>
                                {new Date(msg.sent_at || msg.received_at).toLocaleTimeString(undefined, {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                              {msg.direction === "outbound" && msg.delivery_status && (
                                <DeliveryStatusBadge status={msg.delivery_status} />
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Compose */}
                  <form onSubmit={handleSendMessage} className="flex-none p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    {sendError && (
                      <div className="mb-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {sendError}
                      </div>
                    )}
                    {isOptedOut && (
                      <div className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm flex items-center gap-2">
                        <Ban className="w-4 h-4" />
                        This recipient has opted out of SMS messages
                      </div>
                    )}
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <textarea
                          ref={messageInputRef}
                          value={composeMessage}
                          onChange={(e) => setComposeMessage(e.target.value)}
                          placeholder="Type a message..."
                          rows={2}
                          disabled={isOptedOut}
                          className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none disabled:opacity-50"
                        />
                        <div className="mt-2">
                          <CharacterCounter message={composeMessage} />
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={!composeMessage.trim() || isSending || isOptedOut}
                        className="p-3 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-xl transition-colors disabled:cursor-not-allowed"
                      >
                        {isSending ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                // New Message Compose
                <div className="flex-1 flex flex-col p-6">
                  <div className="max-w-xl mx-auto w-full">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                      New Message
                    </h2>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Phone Number
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="tel"
                            value={composePhone}
                            onChange={(e) => setComposePhone(e.target.value)}
                            placeholder="(555) 123-4567"
                            className="w-full pl-10 pr-4 py-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
                          />
                        </div>
                        {isOptedOut && (
                          <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                            <Ban className="w-4 h-4" />
                            This number has opted out of SMS
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Message
                        </label>
                        <textarea
                          value={composeMessage}
                          onChange={(e) => setComposeMessage(e.target.value)}
                          placeholder="Type your message..."
                          rows={4}
                          disabled={isOptedOut}
                          className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none disabled:opacity-50"
                        />
                        <div className="mt-2">
                          <CharacterCounter message={composeMessage} />
                        </div>
                      </div>

                      {sendError && (
                        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {sendError}
                        </div>
                      )}

                      <button
                        onClick={handleSendMessage}
                        disabled={!composePhone || !composeMessage.trim() || isSending || isOptedOut}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-xl font-medium transition-colors disabled:cursor-not-allowed"
                      >
                        {isSending ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send Message
                          </>
                        )}
                      </button>
                    </div>

                    {/* Quick Templates */}
                    {templates.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          Quick Templates
                        </h3>
                        <div className="space-y-2">
                          {templates.slice(0, 3).map((template) => (
                            <button
                              key={template.template_id}
                              onClick={() => handleUseTemplate(template)}
                              className="w-full text-left p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >
                              <p className="font-medium text-sm text-slate-900 dark:text-white">
                                {template.name}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-1">
                                {template.body}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "templates" && (
          <div className="flex-1 p-6 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Message Templates
                </h2>
                <button
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateForm({ name: "", body: "", description: "", triggerType: "custom" });
                    setShowTemplateModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Template
                </button>
              </div>

              {isLoadingTemplates ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                    No templates yet
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    Create reusable message templates to save time
                  </p>
                  <button
                    onClick={() => setShowTemplateModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Create Template
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map((template) => (
                    <div
                      key={template.template_id}
                      className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-slate-900 dark:text-white">
                              {template.name}
                            </h3>
                            {template.trigger_type && template.trigger_type !== "custom" && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                                {template.trigger_type.replace(/_/g, " ")}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 whitespace-pre-wrap">
                            {template.body}
                          </p>
                          {template.variables?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {template.variables.map((v) => (
                                <span
                                  key={v}
                                  className="px-2 py-0.5 text-xs rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                                >
                                  {`{${v}}`}
                                </span>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            {template.char_count} chars • {Math.ceil(template.char_count / 160)} segment(s)
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleUseTemplate(template)}
                            className="p-2 text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors"
                            title="Use template"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingTemplate(template);
                              setTemplateForm({
                                name: template.name,
                                body: template.body,
                                description: template.description || "",
                                triggerType: template.trigger_type || "custom",
                              });
                              setShowTemplateModal(true);
                            }}
                            className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            title="Edit template"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.template_id)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Delete template"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "stats" && (
          <div className="flex-1 p-6 overflow-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">
                SMS Statistics
              </h2>

              {isLoadingStats ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
                </div>
              ) : stats ? (
                <div className="space-y-6">
                  <div className="flex flex-wrap gap-4">
                    <StatsCard
                      label="Total Sent"
                      value={stats.total_sent || 0}
                      icon={Send}
                      color="sky"
                    />
                    <StatsCard
                      label="Delivered"
                      value={stats.total_delivered || 0}
                      icon={CheckCheck}
                      color="emerald"
                      subValue={`${((stats.delivery_rate || 0) * 100).toFixed(0)}% rate`}
                    />
                    <StatsCard
                      label="Failed"
                      value={stats.total_failed || 0}
                      icon={AlertCircle}
                      color="red"
                    />
                    <StatsCard
                      label="Replies"
                      value={stats.total_replies || 0}
                      icon={MessageCircle}
                      color="purple"
                      subValue={`${((stats.reply_rate || 0) * 100).toFixed(0)}% rate`}
                    />
                    <StatsCard
                      label="Credits Used"
                      value={stats.total_credits_used || 0}
                      icon={Sparkles}
                      color="amber"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <BarChart3 className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No statistics available yet
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {editingTemplate ? "Edit Template" : "New Template"}
              </h2>
              <button
                onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveTemplate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., New Lead Welcome"
                  required
                  className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Message Body *
                </label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  placeholder="Hi {name}, this is {partner_name} from {company}..."
                  required
                  rows={4}
                  className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                />
                <div className="mt-2">
                  <CharacterCounter message={templateForm.body} />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Use {"{variable}"} syntax for dynamic content
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="Brief description of when to use this template"
                  className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Trigger Type
                </label>
                <select
                  value={templateForm.triggerType}
                  onChange={(e) => setTemplateForm({ ...templateForm, triggerType: e.target.value })}
                  className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <option value="custom">Custom</option>
                  <option value="new_lead">New Lead</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="status_update">Status Update</option>
                  <option value="document_needed">Document Needed</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  {editingTemplate ? "Save Changes" : "Create Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMSInbox;
